import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { Map as MlMap } from 'maplibre-gl'

import { MapContainer } from '../map/MapContainer'
import { STYLE_DAY, STYLE_NIGHT, swapTheme } from '../map/theme'
import {
  addUserLocationLayers,
  updateUserLocation,
} from '../map/userLocationLayer'
import { addHighlightLayer } from '../map/highlightMarker'
import { useGeolocation } from '../location/useGeolocation'
import { SearchBar } from '../search/SearchBar'
import { useTheme, type Theme } from '../theme/useTheme'
import { LocateMeFAB } from '../ui/LocateMeFAB'
import { PermissionBanner } from '../ui/PermissionBanner'
import { ThemeCrossfade } from '../ui/ThemeCrossfade'

/**
 * MapView — the primary `/` route, signed-in branch of `<App />`.
 *
 * Composition (z-order per UI-SPEC §Layout & Viewport lines 49-61):
 *   1. MapContainer (full-viewport, z-0)
 *   2. ThemeCrossfade overlay (z-5) — opacity pulse during setStyle
 *   3. search-slot placeholder (z-10) — Plan 02-03 renders SearchBar here
 *   4. PermissionBanner (z-9) — only when perm === 'denied'
 *   5. LocateMeFAB (z-10) — bottom-right
 *
 * Lifecycle wiring:
 *   - Map instance lives in `useState` (set once from MapContainer onMapReady
 *     AFTER 'load' fires, so Effects downstream only see a ready map).
 *   - `useGeolocation` streams fixes.
 *   - `useTheme(fix.lat, fix.lng)` drives day/night — lat/lng null falls back
 *     to Bucharest (D-68).
 *   - An Effect on `[map, theme]` detects theme transitions AFTER first mount:
 *     flips `crossfadeActive` → calls `swapTheme(map, nextUrl, onStyleReady)`
 *     → onStyleReady re-lowers the overlay.
 *   - An Effect on `[map, fix, theme]` calls `updateUserLocation` whenever the
 *     fix or theme changes.
 *   - An Effect on `[map]` attaches `map.on('zoom', ...)` so the accuracy disk
 *     stays true-to-meters as the user pinches (UI-SPEC line 408).
 *
 * MapContext re-exports the map instance so Plan 02-03's SearchBar can
 * `map.flyTo` / `dropHighlightMarker` without prop drilling.
 */

export interface MapContextValue {
  map: MlMap | null
  theme: Theme
}

export const MapContext = createContext<MapContextValue>({
  map: null,
  theme: 'day',
})

export function useMap(): MapContextValue {
  return useContext(MapContext)
}

function ErrorCard({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        width: '100dvw',
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: 'var(--color-text-primary)',
        background: 'var(--color-dominant)',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div
        role="alert"
        style={{
          maxWidth: 420,
          padding: 24,
          borderRadius: 16,
          border: '1px solid var(--color-border)',
          background: 'var(--color-dominant)',
          boxShadow: 'var(--color-shadow)',
        }}
      >
        {children}
      </div>
    </main>
  )
}

export function MapView() {
  const apiKey = import.meta.env.VITE_STADIA_MAPS_API_KEY
  const [map, setMap] = useState<MlMap | null>(null)
  const [crossfadeActive, setCrossfadeActive] = useState(false)

  const { fix, perm } = useGeolocation()
  const theme = useTheme(fix?.lat ?? null, fix?.lng ?? null)

  // Snapshot theme at first render — used as the MapContainer style on mount
  // only. Subsequent theme changes go through swapTheme() on the live map.
  const initialThemeRef = useRef<Theme>(theme)
  const lastThemeRef = useRef<Theme>(theme)

  const handleMapReady = useCallback((m: MlMap) => {
    // Register our own sources + layers BEFORE publishing the map to children
    // so the first `updateUserLocation` never misses the source.
    addUserLocationLayers(m)
    addHighlightLayer(m)
    setMap(m)
  }, [])

  // Theme transitions on the live map (skipping first mount).
  useEffect(() => {
    if (!map || !apiKey) return
    if (theme === lastThemeRef.current) return
    lastThemeRef.current = theme

    const nextUrl = theme === 'night' ? STYLE_NIGHT(apiKey) : STYLE_DAY(apiKey)
    setCrossfadeActive(true)
    swapTheme(map, nextUrl, () => {
      // After the new style commits AND our layers re-register, lower the
      // overlay. Wait one frame so the 150ms fade-in is visible before fade-out.
      window.setTimeout(() => setCrossfadeActive(false), 150)
    })
  }, [map, theme, apiKey])

  // Push every fix onto the user-location source.
  useEffect(() => {
    if (!map || !fix) return
    updateUserLocation(map, fix, theme)
  }, [map, fix, theme])

  // Keep the accuracy disk meters-accurate as the user zooms.
  useEffect(() => {
    if (!map) return
    const handler = () => {
      if (fix) updateUserLocation(map, fix, theme)
    }
    map.on('zoom', handler)
    return () => {
      map.off('zoom', handler)
    }
  }, [map, fix, theme])

  if (!apiKey) {
    return (
      <ErrorCard>
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--font-size-heading)',
            fontWeight: 'var(--font-weight-semibold)',
            lineHeight: 'var(--line-height-heading)',
          }}
        >
          Map unavailable
        </h1>
        <p
          style={{
            marginTop: 12,
            marginBottom: 0,
            fontSize: 'var(--font-size-body)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--line-height-body)',
          }}
        >
          <code>VITE_STADIA_MAPS_API_KEY</code> is missing. Add it to{' '}
          <code>apps/web/.env.local</code> and restart the dev server.
        </p>
      </ErrorCard>
    )
  }

  const initialStyleUrl =
    initialThemeRef.current === 'night' ? STYLE_NIGHT(apiKey) : STYLE_DAY(apiKey)

  return (
    <MapContext.Provider value={{ map, theme }}>
      <MapContainer styleUrl={initialStyleUrl} onMapReady={handleMapReady} />
      <ThemeCrossfade theme={theme} active={crossfadeActive} />
      {/* SearchBar — Plan 02-03 Task 5: sticky-top search with inline dropdown. */}
      <SearchBar />
      {perm === 'denied' && (
        <div
          style={{
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top, 0px) + 76px)',
            left: 12,
            right: 12,
            zIndex: 9,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <PermissionBanner visible />
        </div>
      )}
      <LocateMeFAB map={map} fix={fix} perm={perm} />
    </MapContext.Provider>
  )
}
